import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

export interface StudioNavigationProperties {
  dsExternalType: string;
  dsType: string;
  hasTargetId: boolean;
  pathParamKey: string;
  targetId: string;
  targetIdFromPath: boolean;
}

export interface StudioNavigationProps {
  children: React.ReactNode;
  id: string;
  nodeId: string;
  parentNodeId: string;
  refId: string;
  refType: string;
  uniqueId: string;
  styleText: string;
  className: string;
  properties?: StudioNavigationProperties;
}

export interface StudioNavigationContextValue {
  isOpen: boolean;
  isClosing: boolean;
  onToggle: () => void;
  onOverlayClick: React.MouseEventHandler;
}

const StudioNavigation: React.FC<StudioNavigationProps> = (componentProps) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isClosing, setIsClosing] = React.useState<boolean>(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleToggle = React.useCallback(() => {
    if (!isOpen) {
      // Opening — cancel any in-flight close
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }

      setIsClosing(false);
      setIsOpen(true);
    } else {
      // Closing — play is-closing animations then settle to is-closed
      setIsOpen(false);
      setIsClosing(true);

      closeTimer.current = setTimeout(() => {
        setIsClosing(false);
        closeTimer.current = null;
      }, 650); // matches is-closing animation total defined in Celestial CSS
    }
  }, [isOpen]);

  const handleOverlayClick = React.useCallback<React.MouseEventHandler>(
    (event) => {
      // Menu link anchor, in case we need a different behavior
      // const drawerItem = event.target instanceof Element && event.target.closest<HTMLAnchorElement>('.drawer-item');

      handleToggle();
    },
    [handleToggle],
  );

  React.useEffect(() => {
    return () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);

  const contextValue = React.useMemo<StudioNavigationContextValue>(
    () => ({
      isOpen,
      isClosing,
      onToggle: handleToggle,
      onOverlayClick: handleOverlayClick,
    }),
    [isOpen, isClosing, handleToggle, handleOverlayClick],
  );

  return (
    <ModuleElementDiv className={componentProps.className} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default StudioNavigation;
